import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { useNamespace, useModules } from '../lib/hooks'

export function NamespaceDetail() {
  const { slug = '' } = useParams()
  const [search, setSearch] = useState('')
  const ns      = useNamespace(slug)
  const modules = useModules(slug, search)

  const canAdmin  = ['owner', 'admin'].includes(ns.data?.role ?? '')
  const canMember = ['owner', 'admin', 'member'].includes(ns.data?.role ?? '')

  return (
    <Layout
      title={slug}
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          {canMember && <Link to={`/namespaces/${slug}/publish`} className="btn btn-primary">+ Publish</Link>}
          {canAdmin  && <Link to={`/namespaces/${slug}/settings`} className="btn btn-ghost">Settings</Link>}
        </div>
      }
    >
      {/* Stats */}
      {ns.data && (
        <div className="stat-strip fu" style={{ marginBottom: 24 }}>
          <div className="stat-cell">
            <div className="stat-num">{ns.data.module_count}</div>
            <div className="stat-label">Modules</div>
          </div>
          <div className="stat-cell">
            <div className="stat-num">{ns.data.member_count}</div>
            <div className="stat-label">Members</div>
          </div>
          <div className="stat-cell">
            <span className={`badge ${ns.data.is_private ? 'badge-neutral' : 'badge-green'}`}>
              {ns.data.is_private ? 'private' : 'public'}
            </span>
            {' '}
            <span className={`badge ${ns.data.allow_overwrite ? 'badge-amber' : 'badge-green'}`}>
              {ns.data.allow_overwrite ? 'mutable' : 'immutable'}
            </span>
            <div className="stat-label" style={{ marginTop: 8 }}>Settings</div>
          </div>
          <div className="stat-cell">
            <span className="badge badge-accent">{ns.data.role}</span>
            <div className="stat-label" style={{ marginTop: 8 }}>Your role</div>
          </div>
        </div>
      )}

      {/* Sub-nav */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { label: 'Members',    to: `/namespaces/${slug}/members` },
          { label: 'Bot tokens', to: `/namespaces/${slug}/tokens` },
          { label: 'Analytics',  to: `/namespaces/${slug}/analytics` },
        ].map((l) => (
          <Link key={l.to} to={l.to} className="btn btn-ghost btn-sm">{l.label}</Link>
        ))}
      </div>

      {/* Search */}
      <input
        className="input"
        style={{ maxWidth: 340, marginBottom: 16 }}
        placeholder="Search modules…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {modules.loading && <div className="empty-state">Loading…</div>}
      {modules.error   && <div className="empty-state" style={{ color: 'var(--red)' }}>{modules.error}</div>}

      {(modules.data?.modules.length ?? 0) > 0 && (
        <div className="table-wrap fu">
          <table>
            <thead>
              <tr><th>Module</th><th>Provider</th><th>Latest</th><th>Downloads</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {modules.data!.modules.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>{m.name}</div>
                    {m.description && <div style={{ fontSize: '0.72rem', color: 'var(--text2)' }}>{m.description}</div>}
                  </td>
                  <td><span className="badge badge-neutral">{m.provider}</span></td>
                  <td>
                    {m.latest_version
                      ? <span style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem', color: 'var(--green)' }}>v{m.latest_version}</span>
                      : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </td>
                  <td style={{ color: 'var(--text2)' }}>{m.download_count.toLocaleString()}</td>
                  <td>
                    {m.deprecated
                      ? <span className="badge badge-amber">deprecated</span>
                      : <span className="badge badge-green">active</span>}
                  </td>
                  <td>
                    <Link to={`/namespaces/${slug}/modules/${m.name}/${m.provider}`} className="btn btn-ghost btn-sm">
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!modules.loading && modules.data?.modules.length === 0 && (
        <div className="empty-state fu">
          <div className="empty-icon">⬡</div>
          <div>{search ? `No modules matching "${search}"` : 'No modules yet.'}</div>
          {!search && canMember && (
            <Link to={`/namespaces/${slug}/publish`} className="btn btn-ghost btn-sm" style={{ marginTop: 4 }}>
              Publish first module
            </Link>
          )}
        </div>
      )}
    </Layout>
  )
}
